import React, { useEffect, useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';
import { Button, Form } from 'react-bootstrap';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { FormCol12 } from 'motion-components';
import { FileRepoField } from './filerepo-field';

type FileRepoFieldListProps = {
  label: string;
  fieldName: string;
  arrayFieldPath: string;
  disabled?: boolean;
  /** Path στο Formik errors για required error (π.χ. xrbData.fileDetailsRequired.batteryPhotoFileId). Αν υπάρχει error εδώ, εμφανίζεται στο πρώτο row. */
  requiredErrorPath?: string;
};

type FileDetailRow = {
  fieldName?: string;
  fileId?: number | null;
  sortOrder?: number | null;
  fileRep?: any;
};

const ensureSortedRows = (rows: FileDetailRow[], fieldName: string): FileDetailRow[] => {
  const withSort = rows.map((row, idx) => ({
    ...row,
    fieldName,
    sortOrder: idx + 1,
  }));
  return withSort;
};

const FileRepoFieldListRow = ({
  label,
  fieldName,
  arrayFieldPath,
  absoluteIndex,
  disabled,
  showLabel,
  isVirtual,
  onAddRow,
  onRemoveRow,
  allowRemove,
  requiredError,
}: {
  label: string;
  fieldName: string;
  arrayFieldPath: string;
  absoluteIndex: number;
  disabled: boolean;
  showLabel: boolean;
  isVirtual: boolean;
  onAddRow: () => void;
  onRemoveRow: (absoluteIndex: number) => void;
  allowRemove: boolean;
  requiredError?: string;
}) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const row: FileDetailRow | undefined = getIn(values, `${arrayFieldPath}[${absoluteIndex}]`);
  const fileId = row?.fileId;

  // If user uploads into a "virtual" row (not yet in initialValues),
  // ensure we also persist metadata (fieldName/sortOrder) into the array.
  useEffect(() => {
    if (fileId == null) return;
    const currentFieldName = row?.fieldName;
    const currentSortOrder = row?.sortOrder;

    if (currentFieldName !== fieldName) {
      setFieldValue(`${arrayFieldPath}[${absoluteIndex}].fieldName`, fieldName, false);
    }
    if (currentSortOrder == null) {
      setFieldValue(`${arrayFieldPath}[${absoluteIndex}].sortOrder`, 1, false);
    }
  }, [
    absoluteIndex,
    arrayFieldPath,
    fieldName,
    fileId,
    row?.fieldName,
    row?.sortOrder,
    setFieldValue,
  ]);

  return (
    <div className="mb-1">
      <div className="d-flex align-items-center">
        <div className="flex-grow-1 me-1">
          <FileRepoField
            as={FormCol12}
            label={showLabel ? label : ''}
            fileIdFieldName={`${arrayFieldPath}[${absoluteIndex}].fileId`}
            fileRec={row?.fileRep}
            canEdit={!disabled}
          />
        </div>
        <div className="d-flex align-items-center flex-shrink-0">
          <Button
          variant="success"
          onClick={onAddRow}
          disabled={disabled}
          title="Προσθήκη νέου αρχείου"
          aria-label="Προσθήκη νέου αρχείου"
          style={{
            width: '22px',
            height: '22px',
            padding: 0,
            borderRadius: '999px',
            marginRight: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FaPlus style={{ fontSize: '12px' }} />
        </Button>
        {allowRemove && !isVirtual && (
          <Button
            variant="danger"
            onClick={() => onRemoveRow(absoluteIndex)}
            disabled={disabled}
            title="Διαγραφή αρχείου"
            aria-label="Διαγραφή αρχείου"
            style={{
              width: '22px',
              height: '22px',
              padding: 0,
              borderRadius: '999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FaMinus style={{ fontSize: '12px' }} />
          </Button>
        )}
        </div>
      </div>
      {requiredError && (
        <Form.Control.Feedback
          type="invalid"
          className="d-block"
          style={{ marginTop: '-6px', marginBottom: '6px' }}
        >
          {requiredError}
        </Form.Control.Feedback>
      )}
    </div>
  );
};

export const FileRepoFieldList = ({
  label,
  fieldName,
  arrayFieldPath,
  disabled = false,
  requiredErrorPath,
}: FileRepoFieldListProps) => {
  const { values, setFieldValue, errors } = useFormikContext<any>();
  const allRows: FileDetailRow[] = getIn(values, arrayFieldPath) || [];

  const requiredError =
    requiredErrorPath != null
      ? (getIn(errors, requiredErrorPath) as string | undefined)
      : undefined;

  const matchingRows = useMemo(
    () =>
      allRows
        .map((row, index) => ({ row, index }))
        .filter((item) => item.row?.fieldName === fieldName)
        .sort((a, b) => (a.row?.sortOrder ?? 9999) - (b.row?.sortOrder ?? 9999)),
    [allRows, fieldName],
  );

  // Ensure each field list has its own dedicated row in Formik state.
  // This avoids multiple lists binding to the same virtual index/path.
  useEffect(() => {
    if (matchingRows.length > 0) return;
    setFieldValue(arrayFieldPath, [...allRows, { fieldName, fileId: null, sortOrder: 1 }], false);
  }, [allRows, arrayFieldPath, fieldName, matchingRows.length, setFieldValue]);

  const updateRows = (nextAllRows: FileDetailRow[]) => {
    const keptRows = nextAllRows.filter((row) => row?.fieldName !== fieldName);
    const ownRows = nextAllRows.filter((row) => row?.fieldName === fieldName);
    const normalized = [...keptRows, ...ensureSortedRows(ownRows, fieldName)];
    setFieldValue(arrayFieldPath, normalized);
  };

  const addRow = () => {
    const ownRows = allRows.filter((row) => row?.fieldName === fieldName);
    updateRows([...allRows, { fieldName, fileId: null, sortOrder: ownRows.length + 1 }]);
  };

  const removeRow = (absoluteIndex: number) => {
    const nextAllRows = allRows.filter((_, idx) => idx !== absoluteIndex);
    updateRows(nextAllRows);
  };

  return (
    <div>
      {matchingRows.map(({ index }, idx) => (
        <FileRepoFieldListRow
          key={`${fieldName}-${index}`}
          label={label}
          fieldName={fieldName}
          arrayFieldPath={arrayFieldPath}
          absoluteIndex={index}
          disabled={disabled}
          showLabel={idx === 0}
          isVirtual={false}
          onAddRow={addRow}
          onRemoveRow={removeRow}
          allowRemove={matchingRows.length > 1}
          requiredError={idx === 0 ? requiredError : undefined}
        />
      ))}
    </div>
  );
};
